import "./setupAxios.ts";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import {
  BrandVariants,
  createDarkTheme,
  createLightTheme,
  FluentProvider,
  Theme,
} from "@fluentui/react-components";
import "./index.css";
import App from "./App";
import {
  AccountInfo,
  EventType,
  PublicClientApplication,
} from "@azure/msal-browser";
import { msalConfig } from "@/microsoftAuthConfig";
import "react-loading-skeleton/dist/skeleton.css";
import * as Sentry from "@sentry/react";

Sentry.init({
  dsn: "https://d4b90aaaf4fc6418b11b43534bd95aeb@sentry.kivi.bz.it/60",
  environment: import.meta.env.MODE,
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
});

const fluentTheme: BrandVariants = {
  10: "#070200",
  20: "#281007",
  30: "#44150D",
  40: "#5B1810",
  50: "#741A12",
  60: "#8D1B13",
  70: "#A71915",
  80: "#C21616",
  90: "#CF3527",
  100: "#D84F3B",
  110: "#E06650",
  120: "#E77B65",
  130: "#EE907B",
  140: "#F3A492",
  150: "#F8B8A9",
  160: "#FCCCC0",
};

const lightTheme: Theme = {
  ...createLightTheme(fluentTheme),
  fontFamilyBase: "Geist",
};

const darkTheme: Theme = {
  ...createDarkTheme(fluentTheme),
  fontFamilyBase: "Geist",
};

darkTheme.colorBrandForeground1 = fluentTheme[110];
darkTheme.colorBrandForeground2 = fluentTheme[130];

const msalInstance = new PublicClientApplication(msalConfig);
await msalInstance.initialize();

/* TODO: delete or uncomment
if (
  !msalInstance.getActiveAccount() &&
  msalInstance.getAllAccounts().length > 0
) {
  // Account selection logic is app dependent. Adjust as needed for different use cases.
  msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
}
*/

// Listen for sign-in event and set active account
msalInstance.addEventCallback(async (event) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  if (event.eventType === EventType.LOGIN_SUCCESS && event.payload.account) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const account = event.payload.account as AccountInfo;
    msalInstance.setActiveAccount(account);
  }
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <FluentProvider theme={lightTheme}>
      <App instance={msalInstance} />
    </FluentProvider>
  </StrictMode>,
);
